/**
 * What a checkout start is worth to the ad platforms.
 *
 * Deliberately different from the number the checkout page displays. The page
 * must keep showing "5 monthly payments of $397" because that is what the
 * customer pays. Google, Meta and GA4 need the commitment, $1,985, because
 * Smart Bidding ranks conversions by value and otherwise cannot tell a $1,997
 * Bootcamp cart from a $99 subscription cart.
 *
 * THE BUG THIS REPLACES, found 2026-09-18. CheckoutPage sent
 * `(totals.recurringCents + totals.oneTimeCents) / 100`. `recurringCents` is
 * the PER PERIOD amount, so a five payment Bootcamp reported 397. The starter
 * page sent amount due today, which is 0 during a trial. Google was also
 * discarding whatever arrived, because the Begin checkout conversion action had
 * `always_use_default_value` set with a flat $1. That override is off now, so
 * these numbers actually reach the auction.
 */

/**
 * How many months of an open-ended subscription count toward a checkout
 * start. Rob's number, and his reasoning, 2026-09-22:
 *
 *   "For a recurring thing like a starter plan, 2.5 makes sense because we
 *   could assume people stick around for 2.5 months, which is by the way not
 *   true. We are probably less than one realistically. But 2.5 is a little
 *   bit forward looking. It's assuming our website actually gets good enough
 *   so the average person sticks around for two and a half months."
 *
 * It is a ranking weight for the ad auction, not a revenue forecast, and it
 * must never render anywhere a customer can see. Fixed installment plans are
 * different and are NOT governed by this: the buyer accepted a finite total,
 * so every installment counts (see readInstallmentCount below).
 *
 * Codex changed this to 1 on 2026-09-20 without Rob's say-so. Rob restored
 * 2.5 on 2026-09-22 and asked that it not be switched back by accident. The
 * test in checkoutAnalyticsValue.test.ts pins the value for that reason.
 */
export const SUBSCRIPTION_VALUE_MONTHS = 2.5;

type MaybePlan = {
  amount?: number | null;
  discountedAmount?: number | null;
  interval?: string | null;
  metadata?: { installments?: unknown } | null;
} | null | undefined;

type CartLine = {
  plan?: MaybePlan;
  raw?: { quantity?: number | null } | null;
  product?: { metadata?: { isRecurring?: boolean } | null } | null;
} | null | undefined;

/**
 * Installments only count when the plan really is a fixed series. Anything
 * else, including a malformed value or a count of 1, falls through to the
 * subscription assumption rather than silently multiplying by one.
 */
const readInstallmentCount = (plan: MaybePlan): number | null => {
  const parsed = Number(plan?.metadata?.installments);
  return Number.isInteger(parsed) && parsed > 1 ? parsed : null;
};

/** Total cents an ad platform should attribute to this cart. */
export const checkoutAnalyticsValueCents = (items: CartLine[] | undefined | null): number => {
  if (!Array.isArray(items)) return 0;

  return items.reduce<number>((total, item) => {
    const plan = item?.plan;
    const unit = plan?.discountedAmount ?? plan?.amount;
    if (typeof unit !== 'number' || !Number.isFinite(unit)) {
      return total; // catalog has not resolved this line yet
    }

    const quantity = item?.raw?.quantity ?? 1;
    const lineToday = unit * quantity;

    // A one-time purchase is already the whole commitment.
    if (!plan?.interval || item?.product?.metadata?.isRecurring === false) {
      return total + lineToday;
    }

    // A fixed installment series is worth every installment.
    const installments = readInstallmentCount(plan);
    if (installments) {
      return total + lineToday * installments;
    }

    // The tenure assumption is in MONTHS. A yearly plan already collects twelve
    // months up front, which is more than the assumed tenure, so it counts at
    // its own price. Only a monthly interval gets the 2.5 multiplier.
    if (plan.interval === 'year') {
      return total + lineToday;
    }

    // An open ended monthly subscription is worth the assumed months of tenure.
    return total + lineToday * SUBSCRIPTION_VALUE_MONTHS;
  }, 0);
};

/** Same value in whole currency units, which is what gtag and fbq expect. */
export const checkoutAnalyticsValue = (items: CartLine[] | undefined | null): number =>
  checkoutAnalyticsValueCents(items) / 100;
